import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import AdPlacementGrid from './components/AdPlacementGrid';
import AdPricingTable from './components/AdPricingTable';
import AdPerformanceStats from './components/AdPerformanceStats';
import AdSubmissionForm from './components/AdSubmissionForm';
import AdSenseInfo from './components/AdSenseInfo';
import { useSEO, generateBreadcrumbSchema } from '../../utils/seo';

export default function AdvertiseDisplayPage() {
  const [showForm, setShowForm] = useState(false);
  const [selectedPlacement, setSelectedPlacement] = useState<string | null>(null);

  useSEO({
    title: 'Display Advertising Roseville CA | Banner Ads & Placements | Roseville.org',
    description: 'Purchase premium display ad space on Roseville.org. Reach 50,000+ monthly visitors with banner ads, sidebar placements, and in-content advertising. 12 strategic ad zones with detailed analytics.',
    keywords: 'display advertising Roseville, banner ads Roseville CA, local advertising Roseville.org, ad placements Roseville, digital advertising Placer County',
    canonical: '/advertise-display',
    ogImage: 'https://public.readdy.ai/ai/img_res/2aba41632956fee57d983030ff5d4f76.jpg',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          name: 'Display Advertising — Roseville.org',
          description: 'Premium display advertising opportunities on Roseville.org reaching 50,000+ monthly local visitors.',
          url: `${import.meta.env.VITE_SITE_URL || 'https://roseville.org'}/advertise-display`,
          inLanguage: 'en-US',
        },
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Advertise', url: '/advertise' },
          { name: 'Display Advertising', url: '/advertise-display' },
        ]),
      ],
    },
  });

  const handleSelectPlacement = (placementId: string) => {
    setSelectedPlacement(placementId);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 text-white py-24">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-6">
            <i className="ri-advertisement-line text-lg"></i>
            <span>Premium Advertising Opportunities</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Display Advertising on<br />Roseville.com
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">
            Reach 50,000+ monthly visitors with strategic ad placements across our platform. Choose from premium banner positions, sidebar ads, and in-content placements.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <button
              onClick={() => setShowForm(true)}
              className="bg-white text-emerald-600 px-8 py-4 rounded-lg font-semibold hover:bg-emerald-50 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              Purchase Ad Space
            </button>
            <a
              href="#placements"
              className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/20 transition-all border-2 border-white/30 whitespace-nowrap"
            >
              View Available Slots
            </a>
          </div>
          
          {/* Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <div className="text-3xl font-bold mb-1">50K+</div>
              <div className="text-sm text-white/80">Monthly Visitors</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <div className="text-3xl font-bold mb-1">12</div>
              <div className="text-sm text-white/80">Ad Placements</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <div className="text-3xl font-bold mb-1">1.68%</div>
              <div className="text-sm text-white/80">Average CTR</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <div className="text-3xl font-bold mb-1">255K+</div>
              <div className="text-sm text-white/80">Monthly Impressions</div>
            </div>
          </div>
        </div>
      </section>

      {/* Ad Submission Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full my-8">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-2xl font-bold text-gray-900">Purchase Display Ad</h2>
              <button
                onClick={() => setShowForm(false)}
                className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <AdSubmissionForm 
              selectedPlacement={selectedPlacement}
              onClose={() => setShowForm(false)}
            />
          </div>
        </div>
      )}

      {/* Performance Stats */}
      <AdPerformanceStats />

      {/* Ad Placements Grid */}
      <section id="placements" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Available Ad Placements</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose from strategic positions across our platform. All placements include detailed analytics and performance tracking.
            </p>
          </div>
          <AdPlacementGrid onSelectPlacement={handleSelectPlacement} />
        </div>
      </section>

      {/* Pricing Table */}
      <AdPricingTable />

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Advertise With Us?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Reach engaged local audiences with premium ad placements
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-8 border border-emerald-100">
              <div className="w-14 h-14 flex items-center justify-center bg-emerald-600 text-white rounded-lg mb-6">
                <i className="ri-group-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Targeted Local Audience</h3>
              <p className="text-gray-600 leading-relaxed">
                Reach 50,000+ monthly visitors actively searching for local businesses, events, and services in Roseville and surrounding areas.
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-8 border border-blue-100">
              <div className="w-14 h-14 flex items-center justify-center bg-blue-600 text-white rounded-lg mb-6">
                <i className="ri-line-chart-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Detailed Analytics</h3>
              <p className="text-gray-600 leading-relaxed">
                Track impressions, clicks, CTR, and conversions with our comprehensive analytics dashboard. Optimize your campaigns in real-time.
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-8 border border-purple-100">
              <div className="w-14 h-14 flex items-center justify-center bg-purple-600 text-white rounded-lg mb-6">
                <i className="ri-money-dollar-circle-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Flexible Pricing</h3>
              <p className="text-gray-600 leading-relaxed">
                Choose from monthly packages starting at $200 or premium positions up to $600. No long-term contracts required.
              </p>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-8 border border-orange-100">
              <div className="w-14 h-14 flex items-center justify-center bg-orange-600 text-white rounded-lg mb-6">
                <i className="ri-layout-grid-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Multiple Placement Options</h3>
              <p className="text-gray-600 leading-relaxed">
                12 strategic ad zones including homepage hero, sidebar, in-content, directory listings, and mobile sticky positions.
              </p>
            </div>

            <div className="bg-gradient-to-br from-rose-50 to-red-50 rounded-xl p-8 border border-rose-100">
              <div className="w-14 h-14 flex items-center justify-center bg-rose-600 text-white rounded-lg mb-6">
                <i className="ri-smartphone-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Mobile Optimized</h3>
              <p className="text-gray-600 leading-relaxed">
                All ad placements are fully responsive and optimized for mobile devices, reaching users wherever they browse.
              </p>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl p-8 border border-teal-100">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-600 text-white rounded-lg mb-6">
                <i className="ri-customer-service-2-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Dedicated Support</h3>
              <p className="text-gray-600 leading-relaxed">
                Our team helps you create effective ad creatives, optimize placement strategy, and maximize your ROI.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* AdSense Info */}
      <AdSenseInfo />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Start Advertising?</h2>
          <p className="text-xl text-white/90 mb-8">
            Join local businesses already reaching thousands of engaged Roseville residents every month.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <button
              onClick={() => setShowForm(true)}
              className="bg-white text-emerald-600 px-8 py-4 rounded-lg font-semibold hover:bg-emerald-50 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              Purchase Ad Space Now
            </button>
            <Link
              to="/advertise"
              className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/20 transition-all border-2 border-white/30 whitespace-nowrap"
            >
              View All Advertising Options
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}